
import React, { useState, useEffect } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Mail, 
  Link, 
  FileText, 
  Bell, 
  RefreshCw, 
  ExternalLink, 
  MessageSquare, 
  ShoppingCart 
} from 'lucide-react';
import EmailSettingsTab from './EmailSettingsTab';
import ReferralURLSettingsTab from './ReferralURLSettingsTab';
import TemplateSettingsTab from './TemplateSettingsTab';
import AutoUserEmailTemplateTab from './AutoUserEmailTemplateTab';
import IntegrationsSettingsTab from './IntegrationsSettingsTab';
import PointRedemptionSettingsTab from './PointRedemptionSettingsTab';
import NotificationTemplatesTab from './NotificationTemplatesTab';
import EcommerceSettingsTab from './EcommerceSettingsTab';
import { 
  getReferralURLs, 
  getReferralTemplates 
} from '@/utils/config/referral';

interface AdminSettingsTabsProps {
  activeTab: string;
  onTabChange: (tab: string) => void;
  configs: any;
  onConfigSaved: () => Promise<void>;
}

const AdminSettingsTabs: React.FC<AdminSettingsTabsProps> = ({
  activeTab,
  onTabChange,
  configs,
  onConfigSaved
}) => {
  const [referralUrls, setReferralUrls] = useState([]);
  const [referralTemplates, setReferralTemplates] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  
  // Load data on initial render
  useEffect(() => {
    loadData();
  }, []);
  
  const loadData = async () => {
    setIsLoading(true);
    try {
      // Load URLs and templates from localStorage
      const urls = getReferralURLs();
      const templates = getReferralTemplates();
      
      setReferralUrls(urls);
      setReferralTemplates(templates);
    } catch (error) {
      console.error('Error loading referral data:', error);
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleReferralDataSaved = async () => {
    await loadData();
    await onConfigSaved();
  };
  
  return (
    <Tabs value={activeTab} onValueChange={onTabChange} className="w-full">
      <TabsList className="mb-8 grid w-full grid-cols-4 md:grid-cols-8">
        <TabsTrigger value="email" className="flex items-center">
          <Mail className="mr-2 h-4 w-4" />
          <span className="hidden md:inline">Email</span>
        </TabsTrigger>
        <TabsTrigger value="referralUrls" className="flex items-center">
          <Link className="mr-2 h-4 w-4" />
          <span className="hidden md:inline">URLs</span>
        </TabsTrigger>
        <TabsTrigger value="referralTemplates" className="flex items-center">
          <FileText className="mr-2 h-4 w-4" />
          <span className="hidden md:inline">Templates</span>
        </TabsTrigger>
        <TabsTrigger value="notifications" className="flex items-center">
          <Bell className="mr-2 h-4 w-4" />
          <span className="hidden md:inline">Notifications</span>
        </TabsTrigger>
        <TabsTrigger value="redemption" className="flex items-center">
          <RefreshCw className="mr-2 h-4 w-4" />
          <span className="hidden md:inline">Redemption</span>
        </TabsTrigger>
        <TabsTrigger value="integrations" className="flex items-center">
          <ExternalLink className="mr-2 h-4 w-4" />
          <span className="hidden md:inline">Integrations</span>
        </TabsTrigger>
        <TabsTrigger value="autoUserEmail" className="flex items-center">
          <MessageSquare className="mr-2 h-4 w-4" />
          <span className="hidden md:inline">Auto Email</span>
        </TabsTrigger>
        <TabsTrigger value="ecommerce" className="flex items-center">
          <ShoppingCart className="mr-2 h-4 w-4" />
          <span className="hidden md:inline">E-commerce</span>
        </TabsTrigger>
      </TabsList>
      
      <TabsContent value="email">
        <EmailSettingsTab 
          initialConfig={configs.email}
          onSaved={onConfigSaved}
        />
      </TabsContent>
      
      <TabsContent value="referralUrls">
        <ReferralURLSettingsTab 
          urls={referralUrls}
          onSaved={handleReferralDataSaved}
        />
      </TabsContent>
      
      <TabsContent value="referralTemplates">
        <TemplateSettingsTab 
          templates={referralTemplates}
          urls={referralUrls}
          onSaved={handleReferralDataSaved}
        />
      </TabsContent>
      
      <TabsContent value="notifications">
        <NotificationTemplatesTab 
          onSaved={onConfigSaved}
        />
      </TabsContent>
      
      <TabsContent value="redemption">
        <PointRedemptionSettingsTab 
          onSaved={onConfigSaved}
        />
      </TabsContent>
      
      <TabsContent value="integrations">
        <IntegrationsSettingsTab 
          onSaved={onConfigSaved}
        />
      </TabsContent>
      
      <TabsContent value="autoUserEmail">
        <AutoUserEmailTemplateTab 
          onSaved={onConfigSaved}
        />
      </TabsContent>
      
      <TabsContent value="ecommerce">
        <EcommerceSettingsTab 
          onSaved={onConfigSaved}
        />
      </TabsContent>
    </Tabs>
  );
};

export default AdminSettingsTabs;
